/**
 * Upload Rides Hook
 * 
 * Custom hook for handling ride upload operations with validation, batch processing,
 * progress tracking, and error handling.
 * 
 * @module useUploadRides
 */

import { useState, useCallback } from 'react';
import type { RideSchema } from '../types/simulation';
import { useRides } from '../context/RidesContext';
import { validateRidesForUpload, type RideValidationData } from '../services/rideUatValidator';
import { 
  createRide, 
  searchUser,
  getUserById,
  searchLocation,
  type UserSearchResult,
  type LocationSearchResult,
  type UserListEntry,
} from '../services/rideUatClient';
import type { Environment, UATNetworkSettingsResponse } from '../services/fixedRouteUatClient';
import { exportRideData, transformRideToPayload, formatTimeWithTimezone } from '../services/rideDataExporter';
import { getDefaultRequestTime } from '../utils/dateTimeUtils';
import { handleApiError } from '../utils/errorHandling';
import type { RouteOperationResult } from '../components/map/components/routes-transfer/OperationResults';
import { applyRideAddressPlaceholdersToList } from '../utils/rides/addressPlaceholders';

/**
 * Upload progress for a single ride
 */
export interface RideUploadProgress {
  rideId: string;
  rideName: string;
  status: 'pending' | 'validating' | 'searching' | 'uploading' | 'success' | 'error';
  message: string;
  progress?: number; // 0-100
}

/**
 * Upload state
 */
export interface UploadState {
  /** Whether upload is in progress */
  isUploading: boolean;
  /** Whether validation is in progress */
  isValidating: boolean;
  /** Upload progress for each ride */
  progress: Map<string, RideUploadProgress>;
  /** Upload results */
  results: RouteOperationResult[];
  /** Validation results */
  validation: RideValidationData | null;
  /** Current error */
  error: string | null;
}

/**
 * Hook return type
 */
export interface UseUploadRidesReturn {
  /** Upload state */
  uploadState: UploadState;
  /** Start upload operation */
  startUpload: (
    selectedRideIds: Set<string>,
    prerequisites: {
      jwtToken: string;
      productId: string;
      networkId: number | null;
      networkSettings?: UATNetworkSettingsResponse | null;
      networkUuid?: string; // Network UUID string
      timezone?: string;
      downloadAcknowledged?: boolean;
      environment: Environment;
      serviceAreaCity?: string;
      serviceDate?: string; // Service date for upload (YYYY-MM-DD) - determines which services are available
    }
  ) => Promise<void>;
  /** Validate rides before upload */
  validateRides: (
    selectedRideIds: Set<string>,
    prerequisites: {
      jwtToken: string;
      productId: string;
      networkId: number | null;
      networkUuid?: string;
      timezone?: string;
      downloadAcknowledged?: boolean;
      environment: Environment;
      serviceAreaCity?: string;
    }
  ) => Promise<RideValidationData | null>;
  /** Reset upload state */
  resetUpload: () => void;
}

/**
 * useUploadRides Hook
 * 
 * Manages ride upload operations with validation, batch processing, and progress tracking.
 */
export function useUploadRides(): UseUploadRidesReturn {
  const { rides } = useRides();
  const [uploadState, setUploadState] = useState<UploadState>({
    isUploading: false,
    isValidating: false,
    progress: new Map(),
    results: [],
    validation: null,
    error: null,
  });

  const validateRides = useCallback(async (
    selectedRideIds: Set<string>,
    prerequisites: {
      jwtToken: string;
      productId: string;
      networkId: number | null;
      networkUuid?: string;
      timezone?: string;
      downloadAcknowledged?: boolean;
      environment: Environment;
      serviceAreaCity?: string;
      serviceDate?: string; // Service date for upload (YYYY-MM-DD)
    }
  ): Promise<RideValidationData | null> => {
    if (selectedRideIds.size === 0) {
      return null;
    }

    setUploadState(prev => ({ ...prev, isValidating: true, error: null }));

    try {
      // Get selected rides
      const selectedRides = applyRideAddressPlaceholdersToList(rides.filter(r => {
        const rideId = r.ride_id || r.external_reference_id || '';
        return selectedRideIds.has(rideId);
      }));

      if (selectedRides.length === 0) {
        setUploadState(prev => ({ ...prev, isValidating: false }));
        return null;
      }

      // Search for users and locations for each ride
      const userSearchResultsMap = new Map<string, UserSearchResult[]>();
      const locationSearchResultsMap = new Map<string, { pickup: LocationSearchResult | null; dropoff: LocationSearchResult | null }>();

      // Get network UUID
      const networkUuid = prerequisites.networkUuid;

      if (!networkUuid) {
        throw new Error('Network UUID is required for validation');
      }

      // Search for users and locations
      for (const ride of selectedRides) {
        const rideId = ride.ride_id || ride.external_reference_id || '';
        
        // Search for user
        const userId = ride.user_id || ride.external_user_reference_id;
        if (userId) {
          try {
            // Check if userId is a UUID - if so, try to get user by ID first
            const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
            let userResults: UserSearchResult[] = [];
            
            if (uuidRegex.test(userId)) {
              // Try to get user by ID (for downloaded rides with UUID user_id)
              const userById = await getUserById(
                prerequisites.jwtToken,
                prerequisites.productId,
                networkUuid,
                userId,
                prerequisites.environment
              );
              
              if (userById) {
                // Convert UserListEntry to UserSearchResult format
                userResults = [{
                  id: userById.id,
                  firstName: userById.firstName,
                  lastName: userById.lastName,
                  email: userById.email,
                  displayId: userById.displayId,
                }];
              }
            }
            
            // If not found by ID, try searching by name
            if (userResults.length === 0) {
              userResults = await searchUser(
                prerequisites.jwtToken,
                prerequisites.productId,
                networkUuid,
                userId,
                prerequisites.environment
              );
            }
            
            userSearchResultsMap.set(rideId, userResults);
          } catch (error) {
            console.warn(`[useUploadRides] Failed to search user for ride ${rideId}:`, error);
          }
        }

        // Search for locations
        if (ride.from_address && ride.to_address) {
          try {
            const pickupLocation = await searchLocation(
              prerequisites.jwtToken,
              prerequisites.productId,
              networkUuid,
              ride.from_address,
              prerequisites.serviceAreaCity || '',
              prerequisites.environment
            );
            
            const dropoffLocation = await searchLocation(
              prerequisites.jwtToken,
              prerequisites.productId,
              networkUuid,
              ride.to_address,
              prerequisites.serviceAreaCity || '',
              prerequisites.environment
            );

            locationSearchResultsMap.set(rideId, {
              pickup: pickupLocation,
              dropoff: dropoffLocation,
            });
          } catch (error) {
            console.warn(`[useUploadRides] Failed to search locations for ride ${rideId}:`, error);
          }
        }
      }

      // Validate rides
      const validation = validateRidesForUpload(
        selectedRides,
        userSearchResultsMap,
        locationSearchResultsMap
      );

      setUploadState(prev => ({
        ...prev,
        validation,
        isValidating: false,
      }));

      return validation;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Validation failed';
      setUploadState(prev => ({
        ...prev,
        error: errorMessage,
        isValidating: false,
      }));
      return null;
    }
  }, [rides]);

  const startUpload = useCallback(async (
    selectedRideIds: Set<string>,
    prerequisites: {
      jwtToken: string;
      productId: string;
      networkId: number | null;
      networkSettings?: UATNetworkSettingsResponse | null;
      networkUuid?: string;
      timezone?: string;
      downloadAcknowledged?: boolean;
      environment: Environment;
      serviceAreaCity?: string;
      serviceDate?: string; // Service date for upload (YYYY-MM-DD) - determines which services are available
    }
  ): Promise<void> => {
    if (selectedRideIds.size === 0) {
      return;
    }

    // Get network UUID
    const networkUuid = prerequisites.networkUuid || 
      (prerequisites.networkSettings?.data?.networks?.[0]?.networkSettings?.id);

    if (!networkUuid) {
      setUploadState(prev => ({
        ...prev,
        error: 'Network UUID is required for upload',
      }));
      return;
    }

    setUploadState(prev => ({
      ...prev,
      isUploading: true,
      error: null,
      results: [],
    }));

    // Get selected rides
    const selectedRides = applyRideAddressPlaceholdersToList(rides.filter(r => {
      const rideId = r.ride_id || r.external_reference_id || '';
      return selectedRideIds.has(rideId);
    }));

    // Initialize progress
    const progressMap = new Map<string, RideUploadProgress>();
    selectedRides.forEach(ride => {
      const rideId = ride.ride_id || ride.external_reference_id || '';
      const rideName = `${ride.from_address || 'Pickup'} → ${ride.to_address || 'Dropoff'}`;
      progressMap.set(rideId, {
        rideId,
        rideName,
        status: 'pending',
        message: 'Waiting to upload...',
      });
    });
    setUploadState(prev => ({ ...prev, progress: progressMap }));

    const results: RouteOperationResult[] = [];

    // Process each ride
    for (const ride of selectedRides) {
      const rideId = ride.ride_id || ride.external_reference_id || '';
      const rideName = `${ride.from_address || 'Pickup'} → ${ride.to_address || 'Dropoff'}`;

      try {
        // Update progress: validating
        setUploadState(prev => {
          const newProgress = new Map(prev.progress);
          newProgress.set(rideId, {
            rideId,
            rideName,
            status: 'validating',
            message: 'Validating ride data...',
          });
          return { ...prev, progress: newProgress };
        });

        // Validate ride export
        const exportResult = exportRideData(ride);
        if (!exportResult.isValid) {
          throw new Error(`Validation failed: ${exportResult.errors.join(', ')}`);
        }

        // Update progress: searching
        setUploadState(prev => {
          const newProgress = new Map(prev.progress);
          newProgress.set(rideId, {
            rideId,
            rideName,
            status: 'searching',
            message: 'Searching for user and locations...',
          });
          return { ...prev, progress: newProgress };
        });

        // Search for user
        const userId = ride.user_id || ride.external_user_reference_id;
        if (!userId) {
          throw new Error('User ID is required');
        }

        if (!networkUuid) {
          throw new Error('Network UUID is required for user search');
        }

        // Check if userId is a UUID - if so, try to get user by ID first
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        const isUuid = uuidRegex.test(userId);
        let selectedUser: UserSearchResult | null = null;
        let useUserIdDirectly = false;
        
        if (isUuid) {
          // Try to get user by ID (for downloaded rides with UUID user_id)
          try {
            const userById = await getUserById(
              prerequisites.jwtToken,
              prerequisites.productId,
              networkUuid,
              userId,
              prerequisites.environment
            );
            
            if (userById) {
              // Convert UserListEntry to UserSearchResult format
              selectedUser = {
                id: userById.id,
                firstName: userById.firstName,
                lastName: userById.lastName,
                email: userById.email,
                displayId: userById.displayId,
              };
            } else {
              // User not found in first 200 users, but if it's a UUID from a downloaded ride,
              // we can try using it directly - the API will validate it
              console.warn(`[useUploadRides] User ${userId} not found in first 200 users, will use UUID directly`);
              useUserIdDirectly = true;
            }
          } catch (error) {
            console.warn(`[useUploadRides] Failed to get user by ID ${userId}, will use UUID directly:`, error);
            // For UUIDs, if lookup fails, we'll use the UUID directly
            useUserIdDirectly = true;
          }
        }
        
        // If not found by ID and not a UUID, try searching by name
        if (!selectedUser && !useUserIdDirectly) {
          try {
            const userResults = await searchUser(
              prerequisites.jwtToken,
              prerequisites.productId,
              networkUuid,
              userId,
              prerequisites.environment
            );

            if (userResults.length === 0) {
              throw new Error(`User ${userId} not found. The user may not exist in this network or may have been deleted.`);
            }

            selectedUser = userResults[0];
          } catch (error) {
            // If searchUser also fails, throw the error
            if (error instanceof Error && error.message.includes('not found')) {
              throw error;
            }
            throw new Error(`Failed to search for user ${userId}: ${error instanceof Error ? error.message : String(error)}`);
          }
        }
        
        // If we're using the UUID directly, create a minimal user object
        if (useUserIdDirectly && !selectedUser) {
          selectedUser = {
            id: userId,
            firstName: '',
            lastName: '',
            email: undefined,
            displayId: undefined,
          };
        }
        
        // Ensure selectedUser is not null at this point
        if (!selectedUser) {
          throw new Error(`User ${userId} not found. The user may not exist in this network or may have been deleted.`);
        }

        // Search for locations
        if (!ride.from_address || !ride.to_address) {
          throw new Error('Pickup and dropoff addresses are required');
        }

        const pickupLocation = await searchLocation(
          prerequisites.jwtToken,
          prerequisites.productId,
          networkUuid,
          ride.from_address,
          prerequisites.serviceAreaCity || '',
          prerequisites.environment
        );

        if (!pickupLocation) {
          throw new Error(`Pickup location not found: ${ride.from_address}`);
        }

        const dropoffLocation = await searchLocation(
          prerequisites.jwtToken,
          prerequisites.productId,
          networkUuid,
          ride.to_address,
          prerequisites.serviceAreaCity || '',
          prerequisites.environment
        );

        if (!dropoffLocation) {
          throw new Error(`Dropoff location not found: ${ride.to_address}`);
        }

        // Update progress: uploading
        setUploadState(prev => {
          const newProgress = new Map(prev.progress);
          newProgress.set(rideId, {
            rideId,
            rideName,
            status: 'uploading',
            message: 'Uploading ride...',
            progress: 50,
          });
          return { ...prev, progress: newProgress };
        });

        // Format time with timezone
        const timezone = prerequisites.timezone || 'Australia/Sydney';
        // Use provided service date if available, otherwise extract from formatted time
        const uploadServiceDate = prerequisites.serviceDate;
        const formattedTime = formatTimeWithTimezone(
          ride.request_time || getDefaultRequestTime(),
          timezone,
          uploadServiceDate // Pass service date to override the date part of the time
        );

        // Determine trip type
        const tripType: 'leaveAt' | 'arriveBy' = 
          ride.request_type === 'arriveBy' ? 'arriveBy' : 'leaveAt';

        // Transform to payload
        const payload = transformRideToPayload(
          ride,
          pickupLocation,
          dropoffLocation,
          selectedUser.id,
          tripType,
          formattedTime
        );

        // Use provided service date, or extract from formatted time as fallback
        const serviceDateForFetch = uploadServiceDate || formattedTime.substring(0, 10);
        const endDate = serviceDateForFetch; // Use same date for end date
        
        console.log('[useUploadRides] Using service date:', serviceDateForFetch, 'for ride time:', formattedTime);

        // Fetch service settings to get allowedServices (matching portal behavior)
        let allowedServices: string[] | undefined;
        try {
          const { fetchServiceSettings } = await import('../services/fixedRouteUatClient');
          const { extractUATServices } = await import('../services/fixedRouteUatImporter');
          
          const serviceSettingsResponse = await fetchServiceSettings(
            prerequisites.jwtToken,
            prerequisites.productId,
            serviceDateForFetch,
            endDate,
            prerequisites.environment
          );
          
          const { getEnvironmentDisplayName } = await import('../services/environmentConfig');
          const services = extractUATServices(
            serviceSettingsResponse,
            getEnvironmentDisplayName(prerequisites.environment),
            prerequisites.productId,
            serviceDateForFetch
          );
          
          if (services.length > 0) {
            // Use the original service ID from UAT metadata if available, otherwise use the generated ID
            allowedServices = services.map(s => s.uatMetadata?.originalId || s.id);
            console.log('[useUploadRides] Found services for allowedServices:', allowedServices, 'for service date:', serviceDateForFetch);
          } else {
            console.warn('[useUploadRides] No services found for date', serviceDateForFetch);
          }
        } catch (error) {
          console.warn('[useUploadRides] Failed to fetch service settings for allowedServices:', error);
          // Continue without allowedServices - it may still work
        }

        // Add allowedServices to payload if available
        if (allowedServices && allowedServices.length > 0) {
          (payload as any).allowedServices = allowedServices;
        }

        // Debug: Log the payload being sent
        console.log('[useUploadRides] Upload payload:', {
          rideId,
          formattedTime,
          tripType,
          userId: selectedUser.id,
          pickupAddress: pickupLocation.label,
          dropoffAddress: dropoffLocation.label,
          allowedServices,
          payload: JSON.stringify(payload, null, 2),
        });

        // Create ride
        const createResult = await createRide(
          prerequisites.jwtToken,
          prerequisites.productId,
          networkUuid,
          payload,
          prerequisites.environment
        );

        if (!createResult.success || !createResult.rideId) {
          console.error('[useUploadRides] Ride creation failed:', {
            success: createResult.success,
            rideId: createResult.rideId,
            error: createResult.error,
            message: createResult.message,
            formattedTime,
            payload: JSON.stringify(payload, null, 2),
          });
          throw new Error(createResult.error || 'Ride creation failed');
        }

        // Update progress: success
        setUploadState(prev => {
          const newProgress = new Map(prev.progress);
          newProgress.set(rideId, {
            rideId,
            rideName,
            status: 'success',
            message: `Ride created successfully (ID: ${createResult.rideId})`,
            progress: 100,
          });
          return { ...prev, progress: newProgress };
        });

        results.push({
          routeId: rideId,
          routeName: rideName,
          success: true,
        });
      } catch (error) {
        const errorMessage = handleApiError(error).message;
        
        // Update progress: error
        setUploadState(prev => {
          const newProgress = new Map(prev.progress);
          newProgress.set(rideId, {
            rideId,
            rideName,
            status: 'error',
            message: errorMessage,
          });
          return { ...prev, progress: newProgress };
        });

        results.push({
          routeId: rideId,
          routeName: rideName,
          success: false,
          error: errorMessage,
        });
      }
    }

    setUploadState(prev => ({
      ...prev,
      isUploading: false,
      results,
    }));
  }, [rides]);

  const resetUpload = useCallback(() => {
    setUploadState({
      isUploading: false,
      isValidating: false,
      progress: new Map(),
      results: [],
      validation: null,
      error: null,
    });
  }, []);

  return {
    uploadState,
    startUpload,
    validateRides,
    resetUpload,
  };
}

